import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FaseCategoriaTorneio } from '../../../../services/categoriaTorneioService';
import {
  buscarFaseGrupoPartidasPorFaseCategoriaTorneio,
  FaseGrupoPartidasLite,
  criarFaseGrupoPartidas,
} from '../../../../services/faseGrupoPartidasService';
import { criarFase } from '../../../../services/faseCategoriaTorneioService';
import GroupSection from './GroupSection';
import { buscarCategoriaTorneioPorId } from '../../../../services/categoriaTorneioService';

interface GroupsPhaseTabProps {
  categoriaId: string;
  faseGrupo: FaseCategoriaTorneio | null;
  onFaseCreated: () => void;
  onMatchUpdated?: () => void;
  canManage?: boolean;
  isAdmin?: boolean;
  modeloDisputa?: string | null;
}

const GroupsPhaseTab: React.FC<GroupsPhaseTabProps> = ({
  categoriaId,
  faseGrupo,
  onFaseCreated,
  onMatchUpdated,
  canManage = true,
  isAdmin = false,
  modeloDisputa,
}) => {
  const [grupos, setGrupos] = useState<FaseGrupoPartidasLite[]>([]);
  const [loading, setLoading] = useState(false);
  const [creatingPhase, setCreatingPhase] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);

  const faseGrupoId = useMemo(() => faseGrupo?.id, [faseGrupo?.id]);

  const canEditMatches = isAdmin || (canManage && (!faseGrupo?.status || faseGrupo.status === 'AGENDADA' || faseGrupo.status === 'EM_ANDAMENTO'));

  const loadGrupos = useCallback(async () => {
    if (!faseGrupoId) return;
    setLoading(true);
    try {
      const data = await buscarFaseGrupoPartidasPorFaseCategoriaTorneio(faseGrupoId);
      setGrupos(data);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
      alert('Erro ao carregar grupos');
    } finally {
      setLoading(false);
    }
  }, [faseGrupoId]);

  useEffect(() => {
    if (faseGrupoId) {
      loadGrupos();
    }
  }, [faseGrupoId, loadGrupos]);

  const handleCreatePhase = async () => {
    if (
      !window.confirm(
        'Tem certeza que deseja criar uma fase de grupos? Esta ação criará uma nova fase do tipo GRUPO.',
      )
    ) {
      return;
    }

    setCreatingPhase(true);
    try {
      const categoria = await buscarCategoriaTorneioPorId(categoriaId);
      const ordem = categoria.fasesCategoria.length + 1;
      await criarFase({
        categoriaTorneioId: categoriaId,
        nome: `Fase de Grupos ${ordem}`,
        dataInicio: new Date(),
        dataFim: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 dias
        status: 'AGENDADA',
        ordem,
        tipoGrupoChave: 'GRUPO',
      });
      onFaseCreated();
    } catch (error) {
      console.error('Erro ao criar fase:', error);
      alert('Erro ao criar fase de grupos');
    } finally {
      setCreatingPhase(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!faseGrupo) return;

    const nomeGrupo = prompt('Digite o nome do grupo:');
    if (!nomeGrupo || !nomeGrupo.trim()) {
      return;
    }

    setCreatingGroup(true);
    try {
      await criarFaseGrupoPartidas({
        nome: nomeGrupo.trim(),
        faseCategoriaTorneioId: faseGrupo.id,
      });
      await loadGrupos();
    } catch (error) {
      console.error('Erro ao criar grupo:', error);
      alert('Erro ao criar grupo');
    } finally {
      setCreatingGroup(false);
    }
  };

  if (!faseGrupo) {
    return (
      <div className="mt-4">
        <div className="alert alert-info">
          <p className="mb-3">Nenhuma fase de grupos criada ainda.</p>
          {canManage && (
            <button
              className="btn btn-primary"
              onClick={handleCreatePhase}
              disabled={creatingPhase}
            >
              {creatingPhase ? 'Criando...' : 'Criar Fase de Grupos'}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>Grupos da Fase: {faseGrupo.nome}</h5>
        {canManage && (
          <button
            className="btn btn-primary"
            onClick={handleCreateGroup}
            disabled={creatingGroup}
          >
            {creatingGroup ? 'Criando...' : 'Novo Grupo'}
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      ) : grupos.length === 0 ? (
        <div className="alert alert-info">
          <p className="mb-0">Nenhum grupo criado ainda.</p>
        </div>
      ) : (
        grupos.map((grupo) => (
          <GroupSection
            key={grupo.id}
            grupo={grupo}
            faseCategoriaTorneioId={faseGrupo.id}
            categoriaId={categoriaId}
            onUpdate={() => {
              loadGrupos();
              onMatchUpdated?.();
            }}
            canManage={canManage}
            canEditMatches={canEditMatches}
            modeloDisputa={modeloDisputa}
          />
        ))
      )}
    </div>
  );
};

export default GroupsPhaseTab;

